import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Edit, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOOD_TYPES = ['Brand', 'Storing', 'Inbraak', 'Waterlekkage', 'Stroomuitval', 'Gaslekkage', 'Ongeval', 'Anders'];

const NoodproceduresTab = ({ locatieId, locatieData, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Weet je zeker dat je deze noodprocedure wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/locaties/${locatieId}/noodprocedures/${itemId}`);
      toast.success('Noodprocedure verwijderd');
      onUpdate();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fout bij verwijderen');
    }
  };

  const noodprocedures = locatieData?.noodprocedures || [];

  const getTypeColor = (type) => {
    const colors = {
      'Brand': 'bg-red-100 text-red-700',
      'Storing': 'bg-amber-100 text-amber-700',
      'Inbraak': 'bg-purple-100 text-purple-700',
      'Waterlekkage': 'bg-blue-100 text-blue-700',
      'Stroomuitval': 'bg-yellow-100 text-yellow-700',
      'Gaslekkage': 'bg-orange-100 text-orange-700',
      'Ongeval': 'bg-red-100 text-red-700',
      'Anders': 'bg-slate-100 text-slate-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Noodprocedures</h3>
          <p className="text-sm text-slate-500">{noodprocedures.length} procedures</p>
        </div>
        <Button onClick={handleAdd}>
          <Plus className="w-4 h-4 mr-2" />
          Procedure toevoegen
        </Button>
      </div>

      {noodprocedures.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <AlertTriangle className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500 mb-4">Geen noodprocedures toegevoegd</p>
          <Button onClick={handleAdd}>
            Eerste procedure toevoegen
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {noodprocedures.map((item) => (
            <Card key={item._id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <div>
                      <CardTitle className="text-lg">{item.titel}</CardTitle>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs mt-1 ${getTypeColor(item.type)}`}>
                        {item.type}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item._id)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-slate-600 whitespace-pre-line">
                  {item.instructies}
                </div>
                {item.documentUrl && (
                  <a
                    href={item.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline mt-2 inline-block"
                  >
                    📄 Document bekijken
                  </a>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <NoodprocedureModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingItem(null);
        }}
        item={editingItem}
        locatieId={locatieId}
        onUpdate={onUpdate}
      />
    </div>
  );
};

const NoodprocedureModal = ({ open, onClose, item, locatieId, onUpdate }) => {
  const [formData, setFormData] = useState({
    type: '',
    titel: '',
    instructies: '',
    documentUrl: ''
  });

  React.useEffect(() => {
    if (item) {
      setFormData({
        type: item.type || '',
        titel: item.titel || '',
        instructies: item.instructies || '',
        documentUrl: item.documentUrl || ''
      });
    } else {
      setFormData({
        type: '',
        titel: '',
        instructies: '',
        documentUrl: ''
      });
    }
  }, [item, open]);

  const handleSubmit = async () => {
    if (!formData.type || !formData.titel || !formData.instructies) {
      toast.error('Vul alle verplichte velden in');
      return;
    }

    try {
      if (item) {
        await axios.put(`${API}/locaties/${locatieId}/noodprocedures/${item._id}`, formData);
        toast.success('Noodprocedure bijgewerkt');
      } else {
        await axios.post(`${API}/locaties/${locatieId}/noodprocedures`, formData);
        toast.success('Noodprocedure toegevoegd');
      }
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Fout bij opslaan');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{item ? 'Noodprocedure bewerken' : 'Noodprocedure toevoegen'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Type *</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer type" />
                </SelectTrigger>
                <SelectContent>
                  {NOOD_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Titel *</Label>
              <Input
                value={formData.titel}
                onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
                placeholder="Brandalarm procedure"
              />
            </div>
          </div>

          <div>
            <Label>Instructies *</Label>
            <Textarea
              value={formData.instructies}
              onChange={(e) => setFormData({ ...formData, instructies: e.target.value })}
              placeholder="1. Activeer brandalarm&#10;2. Bel 112&#10;3. Evacueer gebouw&#10;..."
              rows={8}
            />
          </div>

          <div>
            <Label>Document URL</Label>
            <Input
              value={formData.documentUrl}
              onChange={(e) => setFormData({ ...formData, documentUrl: e.target.value })}
              placeholder="https://..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={handleSubmit}>{item ? 'Bijwerken' : 'Toevoegen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoodproceduresTab;
